import * as Joi from 'joi';
import {
  getEmailSchema,
  getJoiObject,
  getMasterCodeObjectDropdownSchema,
  getNricFinJoiObject,
  getPhoneNumberSchema,
  validateDate,
  validateFloor,
  validateHouseBlockNo,
  validateNRIC,
  validatePostalCode,
  validateSingaporeCountry,
  validateStreetName,
  validateUnit,
} from '../../../../utils/JoiUtils';
import {CandidateProfileFormData, CandidateProfileIsDisableInputs} from '../../../../models';

const countryCodeKey = 'country code';
const telephoneKey = 'Telephone No.';
const getNextOfKinContactNumField = (fieldName: string | undefined) =>
    fieldName ?? 'Emergency Contact No.';
const getNextOfKinNameField = (fieldName: string | undefined) =>
    fieldName ?? 'Emergency Contact Name';

const getNextOfKinNameJoiObject = (
    nextOfKinNameField: string | undefined,
    disableValidation = false
) =>
    getJoiObject(
        Joi.string()
        .required()
        .max(66)
        .required()
        .messages({
          'string.empty': `${getNextOfKinNameField(nextOfKinNameField)} is not allowed to be empty.`,
          'string.max': `${getNextOfKinNameField(nextOfKinNameField)} is too long.`,
        }),
        disableValidation
    );

const houseBlkNoValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string()
        .max(10)
        .required()
        .when('postalCode', {
          is: Joi.string().not('000000'),
          then: Joi.custom(validateHouseBlockNo),
        })
        .messages({
          'string.empty': 'House/Block No. is not allowed to be empty.',
          'string.max': 'House/Block No. is too long.',
          'string.pattern.houseBlockNo': 'First character must be number.',
        }),
        isDisableInputs?.houseOrBlkNo
    );

const streetNameValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string().max(32).required().custom(validateStreetName).messages({
          'string.empty': 'Street Name is not allowed to be empty.',
          'string.max': 'Street Name is too long.',
          'string.pattern.streetName': 'First character must be alphabet letter.',
        }),
        isDisableInputs?.streetName
    );

const buildingValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string().max(66).allow('', null).messages({
          'string.max': 'Building is too long.',
        }),
        isDisableInputs?.building
    );

const floorValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string().max(2).allow('', null).custom(validateFloor).messages({
          'string.max': 'Floor is too long.',
          'string.pattern.floor': 'Please enter number.',
        }),
        isDisableInputs?.floor
    );

const unitValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string().max(5).allow('', null).custom(validateUnit).messages({
          'string.max': 'Unit is too long.',
          'string.pattern.unit': 'Please enter number and/or alphabet letter.',
        }),
        isDisableInputs?.unit
    );

const postalCodeValidation = (isDisableInputs?: CandidateProfileIsDisableInputs) =>
    getJoiObject(
        Joi.string().max(6).required().custom(validatePostalCode).messages({
          'string.empty': 'Postal Code is not allowed to be empty.',
          'string.max': 'Postal Code is too long.',
          'string.pattern.postalCode': 'Please enter 6 digits.',
        }),
        isDisableInputs?.postalCode
    );

const getNextOfKinContactNoJoiObject = (
    nextOfKinContactNumField: string | undefined,
    disableValidation = false
) =>
    getJoiObject(
        Joi.when('nextOfKinCountryCode', {
          is: validateSingaporeCountry,
          then: getPhoneNumberSchema(
              false,
              getNextOfKinContactNumField(nextOfKinContactNumField),
              8,
              8
          ),
          otherwise: getPhoneNumberSchema(false, getNextOfKinContactNumField(nextOfKinContactNumField)),
        }),
        disableValidation
    );

const candidateProfileAEISSchema = (
    isDisableInputs?: CandidateProfileIsDisableInputs,
    nextOfKinNameField?: string,
    nextOfKinContactNumField?: string,
    isPassportNo = false
) =>
    Joi.object<CandidateProfileFormData>({
      name: getJoiObject(
          Joi.string().required().max(66).messages({
            'string.empty': 'Name is not allowed to be empty.',
            'string.max': 'Name is too long.',
          }),
          isDisableInputs?.name
      ),
      IdType: getJoiObject(Joi.string().required(), isDisableInputs?.IdType),
      NricFin: getNricFinJoiObject(isDisableInputs?.NricFin),
      finPassportNo: getFinPassportNoJoiObject(isPassportNo, isDisableInputs?.finPassportNo),
      dateOfBirth: getJoiObject(
          Joi.string().required().messages({
            'any.required': 'Date of birth is not allowed to be empty.',
          }),
          isDisableInputs?.dateOfBirth
      ),
      hanyupinyinName: getJoiObject(
          Joi.string().allow('').max(66).messages({
            'string.max': 'Hanyu Pinyin Name is too long.',
          }),
          isDisableInputs?.hanyupinyinName
      ),
      workPassType: getJoiObject(
          Joi.object().required().messages({
            'any.required': 'Please select work pass type.',
            'object.base': 'Please select work pass type.',
          }),
          isDisableInputs?.workPassType
      ),
      gender: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'gender'),
          isDisableInputs?.gender
      ),
      race: getJoiObject(getMasterCodeObjectDropdownSchema(false, 'race'), isDisableInputs?.race),
      citizenship: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'citizenship'),
          isDisableInputs?.citizenship
      ),
      nationality: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'nationality'),
          isDisableInputs?.nationality
      ),
      nationalityIssueDate: getJoiObject(
          Joi.custom((...joiParams) => validateDate(true, ...joiParams)).messages({
            'date.pattern.notValid': 'Nationality issue date must be a valid date.',
            'date.pattern.isAfter': 'Nationality issue date cannot be of future dates.',
          }),
          isDisableInputs?.nationalityIssueDate
      ),
      prStatus: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'PR status'),
          isDisableInputs?.prStatus
      ),
      occupation: getJoiObject(
          getMasterCodeObjectDropdownSchema(true, 'occupation'),
          isDisableInputs?.occupation
      ),
      email: getJoiObject(getEmailSchema('Email'), isDisableInputs?.email),
      mobileCountryCode: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, countryCodeKey),
          isDisableInputs?.mobileCountryCode
      ),
      mobileContactNumber: getJoiObject(
          Joi.when('mobileCountryCode', {
            is: validateSingaporeCountry,
            then: getPhoneNumberSchema(false, 'Mobile No.', 8, 8),
            otherwise: getPhoneNumberSchema(false, 'Mobile No.'),
          }),
          isDisableInputs?.mobileContactNumber
      ),
      telephoneCountryCode: getJoiObject(
          Joi.when('preferredTelephoneOrMobile', {
            is: 'T',
            then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
            otherwise: Joi.when('telephoneContactNumber', {
              is: Joi.string().exist(),
              then: getMasterCodeObjectDropdownSchema(false, countryCodeKey),
              otherwise: getMasterCodeObjectDropdownSchema(true, countryCodeKey),
            }),
          }),
          isDisableInputs?.telephoneCountryCode
      ),
      telephoneContactNumber: getJoiObject(
          Joi.when('telephoneCountryCode', {
            is: validateSingaporeCountry,
            then: Joi.when('preferredTelephoneOrMobile', {
              is: 'T',
              then: getPhoneNumberSchema(false, telephoneKey, 8, 8),
              otherwise: getPhoneNumberSchema(true, telephoneKey, 8, 8),
            }),
            otherwise: Joi.when('preferredTelephoneOrMobile', {
              is: 'T',
              then: getPhoneNumberSchema(false, telephoneKey),
              otherwise: getPhoneNumberSchema(true, telephoneKey),
            }),
          }),
          isDisableInputs?.telephoneContactNumber
      ),
      preferredTelephoneOrMobile: getJoiObject(
          Joi.required(),
          isDisableInputs?.preferredTelephoneOrMobile
      ),
      houseOrBlkNo: houseBlkNoValidation(isDisableInputs),
      streetName: streetNameValidation(isDisableInputs),
      building: buildingValidation(isDisableInputs),
      floor: floorValidation(isDisableInputs),
      unit: unitValidation(isDisableInputs),
      postalCode: postalCodeValidation(isDisableInputs),
      preferredAddress: getJoiObject(Joi.required(), isDisableInputs?.preferredAddress),
      foreignAddress1: getJoiObject(
          Joi.when('preferredAddress', {
            is: 'F',
            then: Joi.string().required().max(60).messages({
              'string.empty':
                  'Foreign Address 1 is not allowed to be empty when foreign address is the preferred address.',
              'string.max': 'Foreign Address 1 is too long.',
            }),
            otherwise: Joi.string().allow('').max(60).messages({
              'string.max': 'Foreign Address 1 is too long.',
            }),
          }),
          isDisableInputs?.foreignAddress1
      ),
      foreignAddress2: getJoiObject(
          Joi.string().allow('').max(60).messages({
            'string.max': 'Foreign Address 2 is too long.',
          }),
          isDisableInputs?.foreignAddress2
      ),
      foreignAddress3: getJoiObject(
          Joi.string().allow('').max(60).messages({
            'string.max': 'Foreign Address 3 is too long.',
          }),
          isDisableInputs?.foreignAddress3
      ),
      intendedResidentialArea: getJoiObject(
          Joi.object().required().messages({
            'any.required': 'Please select residential area.',
            'object.base': 'Please select residential area.',
          }),
          isDisableInputs?.intendedResidentialArea
      ),
      nextOfKinName: getNextOfKinNameJoiObject(nextOfKinNameField, isDisableInputs?.nextOfKinName),
      nextOfKinCountryCode: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, countryCodeKey),
          isDisableInputs?.nextOfKinCountryCode
      ),
      nextOfKinContactNumber: getNextOfKinContactNoJoiObject(
          nextOfKinContactNumField,
          isDisableInputs?.nextOfKinContactNumber
      ),
      nextOfKinRelationshipToCandidate: getJoiObject(
          getMasterCodeObjectDropdownSchema(false, 'Relationship to Candidate'),
          isDisableInputs?.nextOfKinRelationshipToCandidate
      ),
      school: Joi.options({stripUnknown: true})
    });

export default candidateProfileAEISSchema;

const getFinPassportNoJoiObject = (isPassportNo: boolean, isDisable = false) =>
    isPassportNo
        ? getJoiObject(
            Joi.string()
            .when('workPassType.description', {
              is: Joi.string().valid('NOT APPLICABLE'),
              then: Joi.string().allow(''),
              otherwise: Joi.string().required().messages({
                'string.empty': `FIN is not allowed to be empty`,
                'any.required': 'FIN is not allowed to be empty',
              }),
            })
            .invalid(Joi.ref('NricFin'))
            .alphanum()
            .max(9)
            .custom(
                (value, helpers) => validateNRIC(value, helpers, false, true),
                'Validate NRIC/FIN/Passport No'
            )
            .messages({
              'string.alphanum': `FIN should be alphanumeric`,
              'string.max': 'FIN is too long',
              'string.pattern.fin': 'Invalid FIN',
              'string.pattern.startCharError': 'Invalid FIN',
              'any.invalid': 'Please enter a different value for NRIC/FIN and Passport No.',
            }),
            isDisable
        )
        : getJoiObject(
            Joi.string().required().invalid(Joi.ref('NricFin')).alphanum().max(20).messages({
              'string.alphanum': 'Passport No. must only contain alpha-numeric characters.',
              'string.max': 'Passport No. is too long.',
              'string.empty': 'Please select Passport No.',
              'any.required': 'Please select Passport No.',
              'any.invalid': 'Please enter a different value for NRIC/FIN and Passport No.',
            }),
            isDisable
        );
